import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";

import { Card, Page } from "@shopify/polaris";

import { getShopVariantLimit } from "../services/index";
import { authenticateUserAction } from "../store/actions/auth-actions";
import { authActions } from "../store/auth-slice";
import {
  ONBOARDING_EMAIL_SETUP,
  ONBOARDING_PROFILE_SETUP,
  ONBOARDING_BILLING_PLAN,
  ROOT,
  ERROR,
} from "../navigation/CONSTANTS";
import { useState } from "react";
import { getShouldRedirectUserToEmailRecipientsPageFlag, clearShouldRedirectUserToEmailRecipientsPageFlag, getDesiredSettingsGotoTab, clearDesiredSettingsGotoTab, getPostAuthReturnUrl, clearPostAuthReturnUrl } from "../utils/localStorageUtils";
import { PROFILE } from "../navigation/CONSTANTS";
import { ACCOUNT_SETTINGS_GOTO } from "../config/CONSTANTS";

const Auth = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isLoggedIn, status, currentShop } = useSelector((state) => state.auth);
  const [displayMessage, setDisplayMessage] = useState(
    "Authenticating User....."
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shop = queryParams.get("shop");
  const loginToken = queryParams.get("token");
  const onboardingStatus = queryParams.get("status");
  const chargeId = queryParams.get("chargeId") || queryParams.get("charge_id");
  const billingPlan = queryParams.get("billingplan");
  const reauth = queryParams.get("reauth");

  useEffect(() => {
    // Start a fresh auth flow whenever the shop or token changes.
    setDisplayMessage("Authenticating User.....");
    dispatch(authActions.resetAuth());
    dispatch(authenticateUserAction({ username: shop, password: loginToken }));
  }, [dispatch, shop, loginToken]);

  const onLoginSuccess = async () => {
    const res = await getShopVariantLimit();
    setDisplayMessage("Getting products and variants count.");
    console.log("[Auth] onboardingStatus:", onboardingStatus, "| chargeId:", chargeId, "| billingPlan:", billingPlan, "| full params:", location.search);

    if (res.success === true) {
      const { allowed, variantCount, maxVariantLimit } = res.data;

      if (!allowed) {
        dispatch(
          authActions.setStoreLimitStatus({
            status: true,
            count: variantCount,
            maxVariantLimit,
          })
        );
        history.push(`${ERROR}?status=1`);
      } else {
        // If a page saved a return URL before triggering an OAuth scope-grant,
        // honour it for any status that would otherwise land on email setup.
        const postAuthReturnUrl = getPostAuthReturnUrl();
        clearPostAuthReturnUrl();

        let redirect = ROOT;
        switch (onboardingStatus) {
          case "JustInstalled":
            redirect = ONBOARDING_PROFILE_SETUP;
            break;
          case "UserProfileProvided":
            redirect = ONBOARDING_BILLING_PLAN;
            break;
          case "BillingSuccessful":
            redirect = `${ONBOARDING_EMAIL_SETUP}`;
            break;
          case "HistoricalOrderAuthorised":
          case "HistoricalOrderDenied":
            redirect = postAuthReturnUrl || `${ONBOARDING_EMAIL_SETUP}`;
            break;
          case "InventoryTransfersAuthorised":
          case "InventoryTransfersDenied":
            redirect = postAuthReturnUrl || ROOT;
            break;
          case "OnboardingCompleted":
            // chargeId or billingplan are present only when arriving from the billing callback —
            // the backend now advances status straight to OnboardingCompleted after billing.
            if (chargeId || billingPlan) {
              redirect = `${ONBOARDING_EMAIL_SETUP}`;
            } else {
              redirect = ROOT;
            }
            break;
          default:
            redirect = postAuthReturnUrl || ROOT;
            break;
        }

        // Returning from the Location scope re-auth: always land back on the
        // Location settings tab (goto=7), regardless of onboarding status.
        if (reauth === "location") {
          redirect = `${PROFILE}?goto=7`;
        }

        // If Help link requested a specific settings tab, only override when onboarding is completed
        const shouldGoToSettingsTab = getShouldRedirectUserToEmailRecipientsPageFlag();
        if (shouldGoToSettingsTab && onboardingStatus === "OnboardingCompleted") {
          const goto = getDesiredSettingsGotoTab() || ACCOUNT_SETTINGS_GOTO;
          redirect = `${PROFILE}?goto=${goto}`;
        }
        // Clear any pending redirect intent after handling
        clearDesiredSettingsGotoTab();
        clearShouldRedirectUserToEmailRecipientsPageFlag();

        history.push(`${redirect}`);
      }
    }
  };

  useEffect(() => {
    if (!status) return;
    if (isLoggedIn && currentShop === shop) {
      onLoginSuccess();
    }
  }, [status, isLoggedIn, currentShop, shop]);

  return (
    <Page>
      <Card title={displayMessage} sectioned>
        <p></p>
        <p>
          {isLoggedIn === false ? (
            <>
              Error Occurred while Authenticating Your Shop Please{" "}
              <a target="_blank" href="/ui/help">
                Contact Us
              </a>{" "}
            </>
          ) : (
            <>You will be redirected to IFH app shortly </>
          )}
        </p>
      </Card>
    </Page>
  );
};

export default Auth;
